import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Copy, Pause, Play } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import {
  PromoCode,
  PromoCodeErrors,
  PromoCodeStatus,
} from '../../../stores/types/promoCode.interface';
import { ComputedPromotionStatus, Promotion } from '../../../stores/types/promotions.interface';
import { copyToClipboard } from '../../../utils/copyToClipboard.util';
import { Button, ButtonSize, ButtonType, ColorType, Icon, Label, LabelType } from '../../_ui';
import { formatPromotionWindow } from '../promotion.utils';
import PromoCodeStatusPill from './PromoCodeStatusPill.component';
import { hasPromoCodeErrors } from './promoCode.utils';

// A code can only be published individually once its parent promotion is live.
const PUBLISHED_PROMOTION_STATUSES: ReadonlyArray<ComputedPromotionStatus> = [
  ComputedPromotionStatus.ACTIVE,
  ComputedPromotionStatus.SCHEDULED,
  ComputedPromotionStatus.PAUSED,
];

interface PromoCodeSavedRowProps {
  index: number;
  promoCode: PromoCode;
  /**
   * Parent promotion. Its status gates whether a DRAFT code can be published yet, and
   * whether a paused code's "Resume" is enabled (a code can't be resumed while its
   * promotion is paused).
   */
  promotion: Promotion;
  canManage: boolean;
  errors?: PromoCodeErrors;
  // Each handler receives the row's own `promoCode` so the parent can pass
  // referentially-stable callbacks, letting the React.memo wrapper skip re-renders of
  // unaffected rows when a sibling card changes.
  /** Edit a DRAFT code (opens the editable card). */
  onEdit: (promoCode: PromoCode) => void;
  onPublish: (promoCode: PromoCode) => void;
  onPause: (promoCode: PromoCode) => void;
  onResume: (promoCode: PromoCode) => void;
  /** Soft-delete a published code. (DRAFT codes are deleted from inside the edit card.) */
  onArchive: (promoCode: PromoCode) => void;
}

const PromoCodeSavedRowComponent = ({
  index,
  promoCode,
  promotion,
  canManage,
  errors = {},
  onEdit,
  onPublish,
  onPause,
  onResume,
  onArchive,
}: PromoCodeSavedRowProps) => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState('');

  // The redeemable window is the code's own validFrom/validUntil.
  const redeemableText = formatPromotionWindow(promoCode.validFrom, promoCode.validUntil, {
    locale: i18n.language,
    endFallback: t('no_end_date'),
  });

  const renderError = (message?: string) => {
    if (!message) return null;
    return (
      <Label
        className='pt-1'
        type={LabelType.LABEL_S_MEDIUM}
        color={ColorType.NEGATIVE}
        text={message}
      />
    );
  };

  const { status } = promoCode;
  const isDraft = status === PromoCodeStatus.DRAFT;
  const isPausable = status === PromoCodeStatus.ACTIVE || status === PromoCodeStatus.SCHEDULED;
  const isPaused = status === PromoCodeStatus.PAUSED;
  const canPublish = isDraft && PUBLISHED_PROMOTION_STATUSES.includes(promotion.status);
  const canArchive =
    status === PromoCodeStatus.PAUSED ||
    status === PromoCodeStatus.EXPIRED ||
    status === PromoCodeStatus.DEPLETED;
  // A code can't be resumed while its parent promotion is not live — resuming the promotion
  // is what brings its codes back. So the per-code Resume is disabled in that case.
  const isPromotionActiveOrScheduled =
    promotion.status === ComputedPromotionStatus.ACTIVE ||
    promotion.status === ComputedPromotionStatus.SCHEDULED;
  // Archived (DELETED) codes are terminal: shown muted with no actions, only the dark
  // "Archived" status pill at full strength.
  const isArchived = status === PromoCodeStatus.DELETED;

  return (
    <div
      className='flex flex-row items-center justify-between border border-grey2 rounded-lg px-4 py-3'
      data-testid={`promoCodeSavedRow-${index}`}
    >
      <div className='flex flex-col gap-1'>
        <div className='flex flex-row items-center gap-2'>
          <Label
            text={t('promo_code_index', { index })}
            type={LabelType.LABEL_S}
            color={isArchived ? ColorType.GREY4 : ColorType.GREY6}
          />
          <Label
            text={promoCode.code}
            type={LabelType.LABEL_S}
            color={isArchived ? ColorType.GREY4 : ColorType.BLACK}
          />
          {!isArchived && (
            <Icon
              src={Copy}
              size={IconSize.SIZE_16x16}
              color={ColorType.GREY5}
              onClick={() => copyToClipboard(promoCode.code, setCopied)}
              alt={copied ? t('copied') : t('copy')}
            />
          )}
          <PromoCodeStatusPill status={status} />
        </div>
        {redeemableText && (
          <Label
            text={`${t('redeemable')}: ${redeemableText}`}
            type={LabelType.BODY3}
            color={isArchived ? ColorType.GREY4 : ColorType.GREY6}
          />
        )}
        {canManage && hasPromoCodeErrors(errors) && (
          <div className='flex flex-col gap-1 mt-2'>
            {renderError(errors.code)}
            {renderError(errors.validFrom)}
            {renderError(errors.validUntil)}
          </div>
        )}
      </div>

      {canManage && !isArchived && (
        <div className='flex flex-row items-center gap-2'>
          {isDraft && (
            <button
              type='button'
              className='text-denim hover:text-denim_hover text-sm font-semibold font-inter whitespace-nowrap'
              onClick={() => onEdit(promoCode)}
              data-testid={`editPromoCode-${index}`}
            >
              {t('edit_details')}
            </button>
          )}
          {canPublish && (
            <Button
              label={t('publish_code')}
              type={ButtonType.PRIMARY}
              size={ButtonSize.SMALL}
              onClick={() => onPublish(promoCode)}
              // A code can only be published individually once its parent promotion is live
              // but the publish button is also disabled when the promotion is paused since
              // that would be a no-op until the promotion is resumed.
              disabled={!isPromotionActiveOrScheduled || hasPromoCodeErrors(errors)}
              dataTestId={`publishPromoCode-${index}`}
            />
          )}
          {isPausable && (
            <Button
              label={t('pause_code')}
              type={ButtonType.OUTLINE}
              size={ButtonSize.SMALL}
              icon={Pause}
              iconColor={ColorType.GREY6}
              onClick={() => onPause(promoCode)}
              dataTestId={`pausePromoCode-${index}`}
            />
          )}
          {/* For a paused code, the destructive Archive sits before Resume (per design).
              DRAFT codes have no row-level delete — they are deleted from inside the
              edit card instead. */}
          {canArchive && (
            <Button
              label={t('archive')}
              type={ButtonType.DESTRUCTIVE}
              size={ButtonSize.SMALL}
              onClick={() => onArchive(promoCode)}
              dataTestId={`archivePromoCode-${index}`}
            />
          )}
          {isPaused && (
            <Button
              label={t('resume_code')}
              type={ButtonType.OUTLINE}
              size={ButtonSize.SMALL}
              icon={Play}
              iconColor={ColorType.GREY6}
              onClick={() => onResume(promoCode)}
              disabled={!isPromotionActiveOrScheduled}
              dataTestId={`resumePromoCode-${index}`}
            />
          )}
        </div>
      )}
    </div>
  );
};

// Memoized: the parent re-renders on every keystroke in a sibling draft card, but a
// saved row only depends on its own (referentially stable) props.
export const PromoCodeSavedRow = memo(PromoCodeSavedRowComponent);
